import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, Star, Play } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";

function ytEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function ytThumb(url) {
  const yt = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return yt ? `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg` : "";
}

function ReviewCard({ r, idx }) {
  const [playing, setPlaying] = useState(false);
  const embed = ytEmbed(r.video_url);
  const thumb = r.video_thumbnail || ytThumb(r.video_url);
  const hasVideo = !!r.video_url;

  return (
    <article className="card-premium p-8" data-testid={`review-card-${idx}`}>
      {hasVideo && (
        <div className="rounded-xl overflow-hidden mb-5 aspect-video bg-black relative">
          {playing && embed ? (
            <iframe src={`${embed}?autoplay=1`} title={r.name} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
          ) : playing && r.video_url ? (
            <video src={r.video_url} controls autoPlay className="w-full h-full" />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} className="absolute inset-0 flex items-center justify-center group" data-testid={`review-play-${idx}`}>
              {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <span className="relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(212,175,55,0.95)", color: "#0D1B2A" }}>
                <Play size={26} fill="currentColor" />
              </span>
            </button>
          )}
        </div>
      )}
      <Quote size={26} style={{ color: "var(--ct-gold)" }} />
      <p className="mt-4 text-base leading-relaxed font-serif italic" style={{ color: "var(--ct-text)" }}>
        „{r.text}”
      </p>
      <div className="mt-6 pt-6 border-t flex items-center justify-between" style={{ borderColor: "var(--ct-border)" }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center font-serif text-base" style={{ background: "var(--ct-emerald)", color: "var(--ct-gold)", border: "1.5px solid var(--ct-gold)" }}>
            {r.name?.[0] || "?"}
          </div>
          <div>
            <p className="text-sm font-medium">{r.name}</p>
            {r.location && <p className="text-xs" style={{ color: "var(--ct-text-dim)" }}>{r.location}</p>}
          </div>
        </div>
        <div className="flex gap-0.5">
          {Array.from({ length: r.rating || 5 }).map((_, k) => (
            <Star key={k} size={12} fill="var(--ct-gold)" stroke="var(--ct-gold)" />
          ))}
        </div>
      </div>
      {r.image_path && !hasVideo && (
        <img src={`${API_BASE}/public/files?path=${encodeURIComponent(r.image_path)}`} alt="captură recenzie" className="mt-4 rounded-lg w-full" />
      )}
    </article>
  );
}

export default function Reviews() {
  const { sectionContent } = useSite();
  const c = sectionContent("reviews");
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/public/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  if (!c) return null;
  // Fără testimoniale fabricate: dacă nu există recenzii aprobate, secțiunea nu se afișează
  if (!reviews.length) return null;
  const list = reviews;

  return (
    <section data-testid="reviews-section" className="py-24 lg:py-32" style={{ background: "var(--ct-surface-2)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="gold-rule" />
            <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Testimoniale"}</span>
            <span className="gold-rule" />
          </div>
          <h2 data-el="title" data-field="title" className="font-serif text-4xl sm:text-5xl leading-tight">{c.title || "Au lucrat cu mine"}</h2>
          {c.subtitle && <p data-el="subtitle" data-field="subtitle" className="text-base leading-relaxed mt-4" style={{ color: "var(--ct-text-muted)" }}>{c.subtitle}</p>}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((r, i) => <ReviewCard key={r.id || i} r={r} idx={i} />)}
        </div>

        {c.allow_user_submission && (
          <div className="text-center mt-10">
            <Link to="/recenzii/lasa-o-recenzie" className="btn-outline-gold" data-testid="leave-review-cta">
              <Star size={16} /> <span data-field="submit_cta">{c.submit_cta || "Lasă și tu o recenzie"}</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
